/**
 * Compute tryout event status from dates instead of relying on stored status.
 * - cancelled/closed: preserved (manually set by admin)
 * - completed: all tryout days have passed **in the gym's timezone**
 * - open: registration window is currently active
 * - upcoming: tryout date is in the future (default)
 *
 * `timeZone` is required, not defaulted (GH #5084). "Has the last tryout day
 * passed?" is a question about the GYM's calendar, and this ran on both a
 * Vercel server pinned to UTC and in the parent's browser. Building `today`
 * from the runtime's clock rolled the date over at 20:00 Eastern / 17:00
 * Pacific, so on the final day of a tryout an evening registration was refused
 * with "This evaluation has already been completed" while the gym was still
 * running it. A default argument would let a missed call site reintroduce that
 * silently; requiring it makes the compiler name every one.
 *
 * The calendar day is compared as a `YYYY-MM-DD` string, never as a `Date`.
 * That format sorts lexicographically in calendar order, so the comparison has
 * no timezone of its own to get wrong — which is the whole defect this fixes.
 *
 * `registration_opens_at` / `registration_closes_at` are `timestamptz` and are
 * deliberately still compared as absolute instants below. Those were always
 * correct; do not "fix" them into local dates.
 */
import { getTodayIsoDateInTimezone } from '@/utils/timezone/serverLocalDate';

/** Date-only portion of "2026-05-28", "2026-05-28T00:00:00Z" or "2026-05-28 00:00:00+00". */
function toIsoDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const dateOnly = value.split(/[T\s]/)[0];
  return /^\d{4}-\d{2}-\d{2}$/.test(dateOnly) ? dateOnly : null;
}

/**
 * The last day the event actually runs, as YYYY-MM-DD, or null when nothing
 * parses. Multi-day events use their day rows; otherwise the event's own date.
 *
 * Sort ascending and take the last: YYYY-MM-DD sorts in calendar order, so this
 * is a plain string max with no date arithmetic to get wrong. Extracted so
 * computeTryoutStatus stays under the complexity cap.
 */
function latestDay(event: {
  event_date: string;
  tryout_event_days?: { event_date: string }[] | null;
}): string | null {
  const dayRows = event.tryout_event_days ?? [];
  // An empty array is not "has day rows" — fall back to event_date, matching
  // the behaviour before GH #5084.
  const candidates = dayRows.length > 0
    ? dayRows.map((d) => toIsoDate(d.event_date))
    : [toIsoDate(event.event_date)];
  return candidates.filter((d): d is string => d !== null).sort().pop() ?? null;
}

export function computeTryoutStatus(event: {
  status: string;
  event_date: string;
  registration_opens_at?: string | null;
  registration_closes_at?: string | null;
  tryout_event_days?: { event_date: string }[] | null;
}, timeZone: string): string {
  // Preserve manually-set terminal statuses
  if (event.status === 'cancelled' || event.status === 'closed') {
    return event.status;
  }

  const today = getTodayIsoDateInTimezone(timeZone);

  // Determine the latest event date (multi-day support). Plain string max:
  // YYYY-MM-DD sorts in calendar order.
  const latestEventDate = latestDay(event);

  // If the latest tryout day has passed on the gym's calendar, it's completed.
  // An unparseable date is not evidence the event is over, so it falls through
  // to the registration-window check rather than being called completed.
  if (latestEventDate !== null && latestEventDate < today) {
    return 'completed';
  }

  // If registration window is currently open, status is "open".
  // Both bounds are true instants (timestamptz) - compared as instants, not dates.
  const now = new Date();
  if (event.registration_opens_at && event.registration_closes_at) {
    const opensAt = new Date(event.registration_opens_at);
    const closesAt = new Date(event.registration_closes_at);
    if (now >= opensAt && now <= closesAt) {
      return 'open';
    }
  }

  // Tryout date is in the future
  return 'upcoming';
}
